import { CheckboxAtom, colors, Stack, Typography } from '@ui-components/neo.tax';
import React, { ReactElement, memo, useEffect, useState } from 'react';

import {
  AscGroupingTicketOrCollection,
  RdGroupingTicketOrCollection,
  TicketGroupingKind,
} from '@neo.tax/shared/graphql';

import { NOISE_REASONING_PREFIX } from './noise-reasoning-prefix';
import { ReasoningTrigger } from './ReasoningTrigger';
import {
  ControlTopAlign,
  GroupingItemClickableArea,
  GroupingItemCount,
  GroupingItemHeader,
  GroupingItemMetaRow,
  GroupingItemRight,
  GroupingItemTitle,
  GroupingItemTitleBlock,
  NewMatchLabel,
  StyledChevronDownIcon,
} from './ticket-grouping-list.styled';

type GroupingSelection = {
  isGroupingSelected: boolean;
  selectedTicketIds: Set<string>;
};

export interface GroupedTicketsComponentProps {
  groupingTicketOrCollection: AscGroupingTicketOrCollection | RdGroupingTicketOrCollection;
  groupingSelections: Record<string, GroupingSelection>;
  setGroupingSelections: React.Dispatch<React.SetStateAction<Record<string, GroupingSelection>>>;
  searchKeyword: string | null;
  isMoveTicketsDisabled: boolean;
  onTicketCountChange?: (groupingId: string, count: number) => void;
}

interface TicketGroupingItemProps {
  GroupedTicketsComponent: React.ComponentType<GroupedTicketsComponentProps>;
  groupingTicketOrCollection: AscGroupingTicketOrCollection | RdGroupingTicketOrCollection;
  groupingSelections: Record<string, GroupingSelection>;
  setGroupingSelections: React.Dispatch<React.SetStateAction<Record<string, GroupingSelection>>>;
  searchKeyword: string | null;
  isMoveTicketsDisabled: boolean;
  // Whether the project drawer this item is rendered in is a noise (Miscellaneous/Ops) project.
  // Drives the reasoning prefix. Only ASC drawers set this; RD leaves it false.
  isNoiseProject?: boolean;
  onTicketCountChange?: (groupingId: string, count: number) => void;
}

const TicketGroupingItem = ({
  GroupedTicketsComponent,
  groupingSelections,
  groupingTicketOrCollection,
  isMoveTicketsDisabled,
  isNoiseProject = false,
  onTicketCountChange,
  searchKeyword,
  setGroupingSelections,
}: TicketGroupingItemProps): ReactElement => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isExpanded && searchKeyword) {
      setIsExpanded(true);
    }

    if (isExpanded && !searchKeyword) {
      setIsExpanded(false);
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword]);

  const toggleExpansion = (): void => {
    setIsExpanded(!isExpanded);
  };

  const currentSelection = groupingSelections[groupingTicketOrCollection.id];
  const isGroupingTicketSelected = currentSelection?.isGroupingSelected ?? false;

  // Matching reasoning + the "new match" flag only exist on ASC groupings. The backend suppresses
  // both when the user has moved the grouping away from where matching routed it, so their presence
  // is the signal to render — no extra client-side gating needed.
  const matchingReasoning =
    'matchingReasoning' in groupingTicketOrCollection ? groupingTicketOrCollection.matchingReasoning : null;
  const isMatchedInLatestSync =
    'isMatchedInLatestSync' in groupingTicketOrCollection ? groupingTicketOrCollection.isMatchedInLatestSync : false;
  const reasoningText =
    matchingReasoning && isNoiseProject ? `${NOISE_REASONING_PREFIX}${matchingReasoning}` : matchingReasoning;

  const handleGroupingTicketSelection = (checked: boolean): void => {
    setGroupingSelections((prev) => ({
      ...prev,
      [groupingTicketOrCollection.id]: {
        isGroupingSelected: checked,
        selectedTicketIds: new Set(), // Clear individual selections when grouping is selected/deselected
      },
    }));
  };

  return (
    <Stack direction="column">
      {/* Header - clickable to toggle expansion. Top-aligned so the title, count, and "New match"
          form one coherent block per item; generous padding + hover wash separate adjacent items. */}
      <GroupingItemHeader paddingY={3} paddingX={4} alignItems="flex-start" gap={2}>
        {!isMoveTicketsDisabled && (
          <ControlTopAlign>
            <CheckboxAtom
              checked={isGroupingTicketSelected}
              disabled={!!searchKeyword || groupingTicketOrCollection.count === 0}
              onChange={(e): void => {
                e.stopPropagation();
                handleGroupingTicketSelection(e.target.checked);
              }}
            />
          </ControlTopAlign>
        )}
        <GroupingItemClickableArea alignItems="flex-start" gap={2} onClick={toggleExpansion} direction="row">
          <StyledChevronDownIcon $isExpanded={isExpanded} />
          <GroupingItemTitleBlock direction="column" gap={1}>
            <GroupingItemTitle fontSize="sm" fontWeight="medium">
              {groupingTicketOrCollection.name}
            </GroupingItemTitle>
            {groupingTicketOrCollection.kind === TicketGroupingKind.GroupingTicket &&
              groupingTicketOrCollection.identifier && (
                <Typography fontSize="xs" color={colors.gray[500]}>
                  {`${groupingTicketOrCollection.ticketType ?? 'Group'}: ${groupingTicketOrCollection.identifier}`}
                </Typography>
              )}
          </GroupingItemTitleBlock>
          <GroupingItemRight>
            {!searchKeyword && (
              <GroupingItemCount fontSize="sm">{groupingTicketOrCollection.count} tickets</GroupingItemCount>
            )}
            {(isMatchedInLatestSync || reasoningText) && (
              <GroupingItemMetaRow>
                {isMatchedInLatestSync && (
                  // Navy to echo the project drawer header (BLUE variant, colors.blue[600]).
                  <NewMatchLabel fontSize="xs" fontWeight="medium" color={colors.blue[600]}>
                    New match
                  </NewMatchLabel>
                )}
                {reasoningText && <ReasoningTrigger reasoning={reasoningText} />}
              </GroupingItemMetaRow>
            )}
          </GroupingItemRight>
        </GroupingItemClickableArea>
      </GroupingItemHeader>

      {isExpanded && (
        <GroupedTicketsComponent
          groupingTicketOrCollection={groupingTicketOrCollection}
          groupingSelections={groupingSelections}
          setGroupingSelections={setGroupingSelections}
          searchKeyword={searchKeyword}
          isMoveTicketsDisabled={isMoveTicketsDisabled}
          onTicketCountChange={onTicketCountChange}
        />
      )}
    </Stack>
  );
};

export const MemoizedTicketGroupingItem = memo(TicketGroupingItem);
